import { useCallback, useSyncExternalStore } from "react";

/**
 * Subscribe to a CSS media query from React.
 *
 * Used by the table of contents to force its `<details>` open at the `lg`
 * breakpoint (the sticky rail) while leaving it collapsible below it. Reads
 * `window.matchMedia` through `useSyncExternalStore` so the first render already
 * carries the real value (no closed-then-open flash on desktop).
 *
 * Defensive on purpose: jsdom has no `matchMedia`, and some test files install a
 * partial stub without `addEventListener` (tests/web/use-hash-scroll.test.tsx).
 * With no usable `matchMedia` the hook reports `fallback`, so render tests see
 * the desktop layout by default.
 * Source: https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
 */
export function useMediaQuery(query: string, fallback = true): boolean {
	// Stable per query: a new subscribe function would make React resubscribe on
	// every render (the scroll-spy re-renders the TOC on each section change).
	const subscribe = useCallback(
		(onChange: () => void) => {
			const list = window.matchMedia?.(query);
			if (!list || typeof list.addEventListener !== "function") {
				return () => {};
			}
			list.addEventListener("change", onChange);
			return () => list.removeEventListener("change", onChange);
		},
		[query],
	);
	const getSnapshot = useCallback(() => {
		const list = window.matchMedia?.(query);
		return list ? list.matches : fallback;
	}, [query, fallback]);
	return useSyncExternalStore(subscribe, getSnapshot, () => fallback);
}
